import {
  DC_Domain_Models_Usages_UsageAggregation,
  DC_Domain_Models_Usages_UsageInterval,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_Usages_ShortUrlRequest,
  RequestModels_Usages_ShortUrlV2RequestModel,
  ResponseModels_Insights_BelgiumExternalMandates_ShortUrlWithMandateResponse,
  ResponseModels_Insights_Dashboard_InsightsDashboardResponse,
  ResponseModels_Insights_UsagesBe_UsagesModel,
  ResponseModels_Usages_Captar_CaptarDetailResponseModel,
  ResponseModels_Usages_Captar_CaptarUsageResponseModel,
  ResponseModels_Usages_NL_MonthlyEnergyReportResultResponseModel,
  ResponseModels_Usages_NL_ServiceProductVersionResponseModel,
  ResponseModels_Usages_NL_UpdateMandateResponse,
  ResponseModels_Usages_NL_UsagesResponseModel,
  ResponseModels_Usages_SolarForecast_SolarForecastUsagesResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetUsages = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  aggregation?: DC_Domain_Models_Usages_UsageAggregation;
  interval?: DC_Domain_Models_Usages_UsageInterval;
  start?: string;
  end?: string;
  addBudget?: boolean;
  addWeather?: boolean;
  extrapolate?: boolean;
};
/**
 * Returns the new Usages V2 variant model of the usage data.
This now works with ranges, aggregation and interval levels to make the usages service more flexible.
 * 
 * @returns ResponseModels_Usages_NL_UsagesResponseModel Success
 */
export function getUsages(
  { label, customerId, accountId, aggregation, interval, start, end, addBudget, addWeather, extrapolate }: GetUsages,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_NL_UsagesResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/nl/${label}/customers/${customerId}/accounts/${accountId}/usages`,
      query: { aggregation, interval, start, end, addBudget, addWeather, extrapolate },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetUsagesBeV2 = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  aggregation?: DC_Domain_Models_Usages_UsageAggregation;
  interval?: DC_Domain_Models_Usages_UsageInterval;
  start?: string;
  end?: string;
};
/**
 * Get Usages
 *
 * @returns ResponseModels_Insights_UsagesBe_UsagesModel Success
 */
export function getUsagesBeV2(
  { label, customerId, accountId, aggregation, interval, start, end }: GetUsagesBeV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_UsagesBe_UsagesModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/v2/be/${label}/customers/${customerId}/accounts/${accountId}/usages`,
      query: { aggregation, interval, start, end },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetUsagesForAddressBeV2 = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  addressId: string;
  aggregation?: DC_Domain_Models_Usages_UsageAggregation;
  interval?: DC_Domain_Models_Usages_UsageInterval;
  start?: string;
  end?: string;
};
/**
 * Get Usages for a specific address
 *
 * @returns ResponseModels_Insights_UsagesBe_UsagesModel Success
 */
export function getUsagesForAddressBeV2(
  { label, customerId, accountId, addressId, aggregation, interval, start, end }: GetUsagesForAddressBeV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_UsagesBe_UsagesModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/v2/be/${label}/customers/${customerId}/accounts/${accountId}/address/${addressId}/usages`,
      query: { aggregation, interval, start, end },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetServiceProductInsightsForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the insights service status for a customer on one of his accounts.
 *
 * @returns ResponseModels_Usages_NL_ServiceProductVersionResponseModel If process went ok, body contains the a nullable boolean which represents the insights service status.
 */
export function getServiceProductInsightsForCustomer(
  { businessUnit, label, customerId, accountId }: GetServiceProductInsightsForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_NL_ServiceProductVersionResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/insights`,
      errors: { 400: 'If backend returns an error or input is invalid.' },
    },
    requestConfig,
  );
}

type EnableServiceProductInsightsForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Enables the insights service for a customer on one of his accounts.
 *
 * @returns any If process went ok, return 201 created
 */
export function enableServiceProductInsightsForCustomer(
  { businessUnit, label, customerId, accountId }: EnableServiceProductInsightsForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/insights`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'Service product cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type DisableServiceProductInsightsForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Disable the insights service for a customer on one of his accounts.
 *
 * @returns any If the insights service already is deleted, return 208
 */
export function disableServiceProductInsightsForCustomer(
  { businessUnit, label, customerId, accountId }: DisableServiceProductInsightsForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'DELETE',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/insights`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'Service product cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetServiceProductIsmaForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the ISMA servicestatus for a customer on one of his accounts.
 *
 * @returns ResponseModels_Usages_NL_ServiceProductVersionResponseModel If process went ok, body contains the a nullable boolean which represents the insights service status.
 */
export function getServiceProductIsmaForCustomer(
  { businessUnit, label, customerId, accountId }: GetServiceProductIsmaForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_NL_ServiceProductVersionResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/isma`,
      errors: { 400: 'If backend returns an error or input is invalid.' },
    },
    requestConfig,
  );
}

type DisableServiceProductIsmaForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Disable the ISMA service for a customer on one of his accounts.
 *
 * @returns any If the ISMA service already is deleted, return 208
 */
export function disableServiceProductIsmaForCustomer(
  { businessUnit, label, customerId, accountId }: DisableServiceProductIsmaForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'DELETE',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/isma`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'Service product cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type EnableServiceProductIsmaForCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Enables the ISMA service for a customer on one of his accounts.
 *
 * @returns ResponseModels_Usages_NL_UpdateMandateResponse If process went ok, return 201 created
 */
export function enableServiceProductIsmaForCustomer(
  { businessUnit, label, customerId, accountId }: EnableServiceProductIsmaForCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_NL_UpdateMandateResponse> {
  return request(
    {
      method: 'PUT',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/services/isma`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'Service product cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetMonthlyEnergyReportDocumentsByCustomer = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Gets a collection of document info objects belonging to a customer.
Response also holds a property whether the MER is enabled (true), disabled (false) or non-applicable (null).
 * Uses Digital Core Document system API plugged in through Apigee to Macaw's sharepoint endpoint.
 * @returns ResponseModels_Usages_NL_MonthlyEnergyReportResultResponseModel If process went ok, body contains the document info collection.
 */
export function getMonthlyEnergyReportDocumentsByCustomer(
  { businessUnit, label, customerId, accountId }: GetMonthlyEnergyReportDocumentsByCustomer,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_NL_MonthlyEnergyReportResultResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/energyreports`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'If the document collection cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetShortUrlAndExternalMandate = {
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody?: RequestModels_Usages_ShortUrlRequest;
};
/**
 * Get the External Mandates for belgian customers from Fluvius. Includes the shortUrl that a customer can use to change their mandates.
 *
 * @returns ResponseModels_Insights_BelgiumExternalMandates_ShortUrlWithMandateResponse Success
 */
export function getShortUrlAndExternalMandate(
  { label, customerId, accountId, requestBody }: GetShortUrlAndExternalMandate,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_BelgiumExternalMandates_ShortUrlWithMandateResponse> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/be/${label}/customers/${customerId}/accounts/${accountId}/externalmandate/shorturlandmandate`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetShortUrlAndExternalMandateV2 = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  requestBody?: RequestModels_Usages_ShortUrlV2RequestModel;
};
/**
 * Get the External Mandates V2 for belgian customers from Fluvius. Includes the shortUrl that a customer can use to change their mandates.
 *
 * @returns ResponseModels_Insights_BelgiumExternalMandates_ShortUrlWithMandateResponse Success
 */
export function getShortUrlAndExternalMandateV2(
  { businessUnit, label, customerId, accountId, requestBody }: GetShortUrlAndExternalMandateV2,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_BelgiumExternalMandates_ShortUrlWithMandateResponse> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/v2/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/externalmandate/shorturlandmandate`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetInsightsDashboard = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get the Insights Dashboard
 *
 * @returns ResponseModels_Insights_Dashboard_InsightsDashboardResponse Success
 */
export function getInsightsDashboard(
  { businessUnit, label, customerId, accountId }: GetInsightsDashboard,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_Dashboard_InsightsDashboardResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/insights/dashboard`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetInsightsDashboardForAddress = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  addressId: string;
};
/**
 * Get the Insights Dashboard for a specific address
 *
 * @returns ResponseModels_Insights_Dashboard_InsightsDashboardResponse Success
 */
export function getInsightsDashboardForAddress(
  { businessUnit, label, customerId, accountId, addressId }: GetInsightsDashboardForAddress,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Insights_Dashboard_InsightsDashboardResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/address/${addressId}/insights/dashboard`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetCaptarUsages = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  startDate?: string;
  endDate?: string;
};
/**
 * Get Captar usage for BE customers
 *
 * @returns ResponseModels_Usages_Captar_CaptarUsageResponseModel Success
 */
export function getCaptarUsages(
  { businessUnit, label, customerId, accountId, startDate, endDate }: GetCaptarUsages,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_Captar_CaptarUsageResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/captar`,
      query: { startDate, endDate },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetCaptarDetails = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
};
/**
 * Get Captar detailed usage for specific BE customer
 *
 * @returns ResponseModels_Usages_Captar_CaptarDetailResponseModel Success
 */
export function getCaptarDetails(
  { businessUnit, label, customerId, accountId }: GetCaptarDetails,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_Captar_CaptarDetailResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/captar/details`,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetSolarForecastUsages = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  start?: string;
  end?: string;
};
/**
 * Get solar forecast usage for a specific BE customer
 *
 * @returns ResponseModels_Usages_SolarForecast_SolarForecastUsagesResponseModel Success
 */
export function getSolarForecastUsages(
  { businessUnit, label, customerId, accountId, start, end }: GetSolarForecastUsages,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_SolarForecast_SolarForecastUsagesResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/usages/solar-forecast`,
      query: { start, end },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetSolarForecastUsagesForAddress = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  addressId: string;
  start?: string;
  end?: string;
};
/**
 * Get solar forecast usage for a specific BE customer on a specific address
 *
 * @returns ResponseModels_Usages_SolarForecast_SolarForecastUsagesResponseModel Success
 */
export function getSolarForecastUsagesForAddress(
  { businessUnit, label, customerId, accountId, addressId, start, end }: GetSolarForecastUsagesForAddress,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Usages_SolarForecast_SolarForecastUsagesResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/address/${addressId}/usages/solar-forecast`,
      query: { start, end },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
